import { COUPON_ENDPOINTS } from "../constants/billingEndpoints";
import { buildPaginationRequest, getTotalPages } from "../utils/pagination";
import {
  assertSuccessfulResponse,
  buildCollectionResult,
  extractEntity,
} from "../utils/response";

const BILLING_DEBUG = process.env.NODE_ENV !== "production";

const normalizeCoupon = (coupon = {}) => ({
  ...coupon,
  isActive:
    typeof coupon?.isActive === "boolean"
      ? coupon.isActive
      : Boolean(coupon?.active),
  active:
    typeof coupon?.active === "boolean"
      ? coupon.active
      : Boolean(coupon?.isActive),
});

export const createCouponsService = (apiClient) => ({
  async list({ page = 1, pageSize = 10 } = {}) {
    const requestPayload = buildPaginationRequest({
      page,
      pageSize,
      orderBy: "id",
    });

    const resolveListResponse = async (endpoint) => {
      const response = await apiClient.post(endpoint, requestPayload, {
        timeout: 8000,
      });

      if (BILLING_DEBUG) {
        console.info("[billing:Coupon] raw list payload keys", Object.keys(response?.data || {}));
      }

      return buildCollectionResult({
        payload: response.data || {},
        preferredKeys: ["coupons"],
        pageSize,
        normalizeItem: normalizeCoupon,
        fallbackMessage: "Unable to load coupons right now.",
        getTotalPages,
      });
    };

    try {
      return await resolveListResponse(COUPON_ENDPOINTS.list);
    } catch (error) {
      if (COUPON_ENDPOINTS.list === COUPON_ENDPOINTS.create) {
        throw error;
      }

      return resolveListResponse(COUPON_ENDPOINTS.create);
    }
  },

  async create(couponPayload) {
    const response = await apiClient.post(COUPON_ENDPOINTS.create, couponPayload);
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to create coupon."
    );

    return normalizeCoupon(extractEntity(payload, ["coupon"]));
  },

  async update(couponPayload) {
    const response = await apiClient.put(COUPON_ENDPOINTS.update, couponPayload);
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to update coupon."
    );

    return normalizeCoupon(extractEntity(payload, ["coupon"]));
  },

  async getByCode(couponCode) {
    const response = await apiClient.get(
      COUPON_ENDPOINTS.detailsByCode(couponCode)
    );
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to load coupon."
    );

    return normalizeCoupon(extractEntity(payload, ["coupon"]));
  },

  async remove(id) {
    await apiClient.delete(COUPON_ENDPOINTS.remove(id));
    return true;
  },

  async toggleStatus(id, isActive) {
    const response = await apiClient.patch(
      COUPON_ENDPOINTS.toggleStatus(id, isActive),
      null
    );
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to update coupon status."
    );

    return normalizeCoupon(extractEntity(payload, ["coupon"]));
  },

  async validate(payload) {
    const response = await apiClient.post(COUPON_ENDPOINTS.validate, payload);
    return assertSuccessfulResponse(response.data, "Unable to validate coupon.");
  },

  async apply(payload) {
    const response = await apiClient.post(COUPON_ENDPOINTS.apply, payload);
    return assertSuccessfulResponse(response.data, "Unable to apply coupon.");
  },
});
